import { LineSegments, Mesh, MeshBasicMaterial, Shape, ShapeGeometry } from 'three';
import { calArcWallShapePoints } from '@/editor/utils';
import { pixelRatio2d } from '@/editor/math/const';
import { disposeAllChildren } from '../utils';
import {
  ENTITY_RENDER_ORDER,
  errorHighlightedColor,
  selectedOutlineColor,
  separateWallBGColor,
  virtual2dWallColor
} from '@/editor/constant/entity';
import { globalStores } from '@/editor/stores';
import BaseModel2d from './BaseModel2d';
import { WallBaseData } from '@/editor/data/wall/wallBaseData';
import { IShapePoint2d } from '@/editor/types/shape';

type IBaseModelParams = Pick<WallBaseData, 'start' | 'end' | 'thickness' | 'center' | 'clockwise'> & {
  style?: EVirtualWallStyle;
  status?: EVirtualWallStatus;
};

type RequiredBaseModelParams = Required<IBaseModelParams>;

import { BufferGeometry, Line, Vector3 } from 'three';
import { calAuxiliarySegsCore, getBaseShape, shapeSegToPoints } from '@/editor/utils/wallShape';
import { styleToSepWallType } from '@/editor/utils/wall';
import { IPoint2d } from '@/editor/math/IPoint2d';

function toVec3(point: IPoint2d) {
  return new Vector3(point.x / pixelRatio2d, point.y / pixelRatio2d, 0);
}

// 将折线点转换为首尾相接的线段端点对（用于 LineSegments）
function polylineToSegmentPoints(points: IPoint2d[]): IPoint2d[] {
  const segs: IPoint2d[] = [];
  for (let i = 0; i < points.length - 1; i++) {
    segs.push(points[i], points[i + 1]);
  }
  return segs;
}

// 计算隔墙的边界辅助线（玻璃/半玻璃的分隔线），与 separateWallModel 共用 calAuxiliarySegsCore。
// 预览不传入 shape，分隔线不裁剪到墙体轮廓（保持绘制 ghost 的外观）。
function calVirtualAuxiliaryPolylines(params: IBaseModelParams): IPoint2d[][] {
  const segs = calAuxiliarySegsCore({
    start: params.start,
    end: params.end,
    thickness: params.thickness,
    type: styleToSepWallType(params.style),
    center: params.center,
    clockwise: params.clockwise
  });
  return segs.map(shapeSegToPoints);
}

export enum EVirtualWallStyle {
  Wall,
  SepWall,
  VirtualSepWall,
  GlassSepWall,
  HalfGlassSepWall
}

export enum EVirtualWallStatus {
  Normal = 'normal',
  Unnormal = 'unnormal'
}

class VirtualWallModel extends BaseModel2d {
  constructor() {
    super();

    this.setDisplayOrder(ENTITY_RENDER_ORDER.virtualWall);
    globalStores.editorStateStore.sceneInfo.scene2d.scene.add(this._modelGroup);
  }

  private createSingleWall(params: IBaseModelParams) {
    let shapePts: IShapePoint2d[];
    if (params.center) {
      // 起点、终点调换，因为计算时是按顺时针方向计算的
      shapePts = calArcWallShapePoints(params as RequiredBaseModelParams);
    } else {
      const { baseShape } = getBaseShape(params);
      shapePts = baseShape;
    }

    const shape = new Shape();
    for (let i = 0; i < shapePts.length; i++) {
      if (i === 0) shape.moveTo(shapePts[i].x / pixelRatio2d, shapePts[i].y / pixelRatio2d);
      shape.lineTo(shapePts[i].x / pixelRatio2d, shapePts[i].y / pixelRatio2d);
    }

    const outlineColor = params.status === EVirtualWallStatus.Unnormal ? errorHighlightedColor : selectedOutlineColor;
    switch (params.style) {
      case EVirtualWallStyle.SepWall: {
        const fillGeometry = new ShapeGeometry(shape);
        const fillMaterial = new MeshBasicMaterial({
          color: separateWallBGColor,
          transparent: true,
          opacity: 0.6
        });
        const fillMesh = new Mesh(fillGeometry, fillMaterial);
        this._modelGroup.add(fillMesh);

        const outlineGmt = new BufferGeometry();
        const _points = [...shapePts, shapePts[0]];
        outlineGmt.setFromPoints(_points.map((p) => new Vector3(p.x / pixelRatio2d, p.y / pixelRatio2d, 0)));
        const material = new MeshBasicMaterial({ color: outlineColor, transparent: false });
        const outline = new Line(outlineGmt, material);
        this._modelGroup.add(outline);
        break;
      }
      case EVirtualWallStyle.VirtualSepWall: {
        const [polyline = [params.start, params.end]] = calVirtualAuxiliaryPolylines(params);
        const lineGmt = new BufferGeometry();
        lineGmt.setFromPoints(polyline.map(toVec3));
        const material = new MeshBasicMaterial({ color: outlineColor, transparent: false });
        const line = new Line(lineGmt, material);
        this._modelGroup.add(line);
        break;
      }
      case EVirtualWallStyle.HalfGlassSepWall: {
        const fillGeometry = new ShapeGeometry(shape);
        const fillMaterial = new MeshBasicMaterial({
          color: separateWallBGColor,
          transparent: true,
          opacity: 0.6
        });
        const fillMesh = new Mesh(fillGeometry, fillMaterial);
        this._modelGroup.add(fillMesh);

        const _points: IPoint2d[] = [];
        for (let i = 0; i < shapePts.length; i++) {
          _points.push(shapePts[i], shapePts[(i + 1) % shapePts.length]);
        }
        for (const polyline of calVirtualAuxiliaryPolylines(params)) {
          _points.push(...polylineToSegmentPoints(polyline));
        }
        const outlineGmt = new BufferGeometry();
        outlineGmt.setFromPoints(_points.map(toVec3));
        const material = new MeshBasicMaterial({ color: outlineColor, transparent: false });
        const outline = new LineSegments(outlineGmt, material);
        this._modelGroup.add(outline);
        break;
      }
      case EVirtualWallStyle.GlassSepWall: {
        const fillGeometry = new ShapeGeometry(shape);
        const fillMaterial = new MeshBasicMaterial({
          color: separateWallBGColor,
          transparent: true,
          opacity: 0.6
        });
        const fillMesh = new Mesh(fillGeometry, fillMaterial);
        this._modelGroup.add(fillMesh);

        const _points: IPoint2d[] = [];
        for (let i = 0; i < shapePts.length; i++) {
          _points.push(shapePts[i], shapePts[(i + 1) % shapePts.length]);
        }
        for (const polyline of calVirtualAuxiliaryPolylines(params)) {
          _points.push(...polylineToSegmentPoints(polyline));
        }
        const outlineGmt = new BufferGeometry();
        outlineGmt.setFromPoints(_points.map(toVec3));
        const material = new MeshBasicMaterial({ color: outlineColor, transparent: false });
        const outline = new LineSegments(outlineGmt, material);
        this._modelGroup.add(outline);
        break;
      }
      default: {
        const fillGeometry = new ShapeGeometry(shape);
        const fillMaterial = new MeshBasicMaterial({
          color: virtual2dWallColor,
          transparent: true,
          opacity: 0.6
        });
        const fillMesh = new Mesh(fillGeometry, fillMaterial);
        this._modelGroup.add(fillMesh);
      }
    }
  }

  update(walls: IBaseModelParams[]) {
    this.clearVirtualModel();

    for (let i = 0; i < walls.length; i++) {
      const wall = walls[i];
      this.createSingleWall(wall);
    }
  }

  private clearVirtualModel() {
    disposeAllChildren(this._modelGroup);
  }
}

export default VirtualWallModel;
